import * as PIXI from "pixi.js";
import type {
  RendererSettings,
  RenderSnapshot,
  RendererDebugState,
  VisualEvent,
} from "@/ui/components/mythic/board2/render/types";
import { AssetManager } from "@/ui/components/mythic/board2/render/AssetManager";
import { biomeSkinFor, pickBiomeProps } from "@/ui/components/mythic/board2/render/BiomeSkinRegistry";
import { EntityRenderer } from "@/ui/components/mythic/board2/render/EntityRenderer";
import { TelegraphRenderer } from "@/ui/components/mythic/board2/render/TelegraphRenderer";
import { ParticleSystem } from "@/ui/components/mythic/board2/render/Particles/ParticleSystem";
import { FloatingTextSystem } from "@/ui/components/mythic/board2/render/FloatingText/FloatingTextSystem";
import { CameraDirector } from "@/ui/components/mythic/board2/render/CameraDirector";
import { TransitionDirector } from "@/ui/components/mythic/board2/render/TransitionDirector";
import { DevOverlay } from "@/ui/components/mythic/board2/render/DevOverlay";
import { MYTHIC_ASSET_FALLBACKS, MYTHIC_ATLAS_ENTRIES } from "@/ui/components/mythic/board2/render/assetManifest";
import { seededRange } from "@/ui/components/mythic/board2/render/deterministic";

interface TimedDisplay {
  display: PIXI.Container;
  ageMs: number;
  lifeMs: number;
}

interface AmbientParticle {
  sprite: PIXI.Sprite;
  vx: number;
  vy: number;
  baseAlpha: number;
  pulseRate: number;
  pulseAmplitude: number;
  phase: number;
}

function importanceScale(level: VisualEvent["fxImportance"]): number {
  if (level === "critical") return 1.35;
  if (level === "high") return 1.15;
  if (level === "low") return 0.78;
  return 1;
}

function qualityScale(qualityMode: RendererSettings["qualityMode"]): number {
  if (qualityMode === "max") return 1.16;
  if (qualityMode === "perf") return 0.72;
  return 1;
}

function defaultSettings(): RendererSettings {
  return {
    fastMode: false,
    cinematicCamera: true,
    showDevOverlay: false,
    reducedMotion: false,
    animationIntensity: "normal",
    cameraShakeScale: 1,
    particleScale: 1,
    transitionDurationMs: 560,
    eventCadenceMs: 104,
    timedFxDurationScale: 1,
    floatingTextDurationScale: 1,
    qualityMode: "balanced60",
    uiDensity: "minimal",
    tokenLabelMode: "compact",
    fitMode: "adaptive_contain",
    edgePaddingPx: 10,
    safeInsetTopPx: 0,
    safeInsetBottomPx: 0,
    backgroundFill: 0x181111,
  };
}

function tileCenter(snapshot: RenderSnapshot, tile: { x: number; y: number }): { x: number; y: number } {
  const size = snapshot.board.tileSize;
  return {
    x: (tile.x * size) + (size / 2),
    y: (tile.y * size) + (size / 2),
  };
}

function entityCenter(snapshot: RenderSnapshot, entityId: string | undefined): { x: number; y: number } | null {
  if (!entityId) return null;
  const entity = snapshot.entities.find((entry) => entry.id === entityId);
  if (!entity) return null;
  return tileCenter(snapshot, { x: entity.x, y: entity.y });
}

export class BoardRenderer {
  readonly app: PIXI.Application;
  readonly canvas: HTMLCanvasElement;

  private root = new PIXI.Container();
  private backdropLayer = new PIXI.Graphics();
  private world = new PIXI.Container();

  private tilesLayer = new PIXI.Container();
  private terrainLayer = new PIXI.Container();
  private propsLayer = new PIXI.Container();
  private atmosphereLayer = new PIXI.Container();
  private vfxLayer = new PIXI.Container();
  private uiLayer = new PIXI.Container();
  private transitionLayer = new PIXI.Graphics();

  private assetManager: AssetManager;
  private entityRenderer = new EntityRenderer();
  private telegraphRenderer = new TelegraphRenderer();
  private particles = new ParticleSystem();
  private floatingText = new FloatingTextSystem();
  private camera = new CameraDirector();
  private transitions = new TransitionDirector();
  private devOverlay = new DevOverlay();

  private settings: RendererSettings;
  private snapshot: RenderSnapshot | null = null;
  private pendingEvents: VisualEvent[] = [];
  private consumedEventIds = new Set<string>();
  private activeTimed: TimedDisplay[] = [];
  private atmosphereParticles: AmbientParticle[] = [];

  private needsStaticRedraw = true;
  private elapsedMs = 0;
  private fpsWindow: number[] = [];
  private debugState: RendererDebugState = {
    fps: 0,
    drawCalls: 0,
    eventTimeline: [],
    queueDepth: 0,
    activeParticles: 0,
    activeFloatingTexts: 0,
    uiDensity: "minimal",
    tokenLabelMode: "compact",
    statusChipMode: "none",
    intentChipMode: "none",
    cameraScale: 1,
    cameraShakeMs: 0,
  };

  private eventAccumulatorMs = 0;

  private constructor(app: PIXI.Application, canvas: HTMLCanvasElement, settings?: Partial<RendererSettings>) {
    this.app = app;
    this.canvas = canvas;
    this.settings = { ...defaultSettings(), ...(settings ?? {}) };
    this.assetManager = new AssetManager(app.renderer);
    this.assetManager.setFallbackManifest(MYTHIC_ASSET_FALLBACKS);
    void this.assetManager.loadAtlas(MYTHIC_ATLAS_ENTRIES);

    this.root.eventMode = "none";
    this.backdropLayer.eventMode = "none";
    this.world.eventMode = "none";
    this.tilesLayer.eventMode = "none";
    this.terrainLayer.eventMode = "none";
    this.propsLayer.eventMode = "none";
    this.atmosphereLayer.eventMode = "none";
    this.vfxLayer.eventMode = "none";
    this.uiLayer.eventMode = "none";

    this.world.addChild(this.tilesLayer);
    this.world.addChild(this.terrainLayer);
    this.world.addChild(this.propsLayer);
    this.world.addChild(this.atmosphereLayer);
    this.world.addChild(this.entityRenderer.container);
    this.world.addChild(this.telegraphRenderer.container);
    this.world.addChild(this.vfxLayer);
    this.world.addChild(this.particles.container);
    this.world.addChild(this.floatingText.container);
    this.world.addChild(this.uiLayer);

    this.root.addChild(this.backdropLayer);
    this.root.addChild(this.world);
    this.root.addChild(this.transitionLayer);
    this.root.addChild(this.devOverlay.container);

    this.app.stage.addChild(this.root);
  }

  static async mount(host: HTMLElement, settings?: Partial<RendererSettings>): Promise<BoardRenderer> {
    const app = new PIXI.Application();
    await app.init({
      width: Math.max(300, Math.floor(host.clientWidth || 320)),
      height: Math.max(260, Math.floor(host.clientHeight || 300)),
      antialias: true,
      backgroundAlpha: 0,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
      autoStart: false,
      sharedTicker: false,
    });
    host.appendChild(app.canvas);
    return new BoardRenderer(app, app.canvas as HTMLCanvasElement, settings);
  }

  setSettings(next: Partial<RendererSettings>) {
    this.settings = { ...this.settings, ...next };
    this.needsStaticRedraw = true;
  }

  setSnapshot(snapshot: RenderSnapshot) {
    const previousType = this.snapshot?.board.type;
    this.snapshot = snapshot;
    this.needsStaticRedraw = true;

    const worldW = snapshot.board.width * snapshot.board.tileSize;
    const worldH = snapshot.board.height * snapshot.board.tileSize;
    this.camera.setWorld(worldW, worldH);

    if (previousType && previousType !== snapshot.board.type) {
      this.transitions.start(previousType, snapshot.board.type, this.settings);
      this.consumedEventIds.clear();
    }

    const active = snapshot.entities.find((entity) => entity.isActive);
    if (active) {
      const center = tileCenter(snapshot, { x: active.x, y: active.y });
      this.camera.focus(center.x, center.y);
    }
  }

  enqueueEvents(events: VisualEvent[]) {
    if (!Array.isArray(events) || events.length === 0) return;
    for (const event of events) {
      if (this.consumedEventIds.has(event.id)) continue;
      this.pendingEvents.push(event);
    }
    this.pendingEvents.sort((a, b) => {
      if (a.tick !== b.tick) return a.tick - b.tick;
      if (a.sequence !== b.sequence) return a.sequence - b.sequence;
      if (a.createdAt !== b.createdAt) return a.createdAt.localeCompare(b.createdAt);
      return a.id.localeCompare(b.id);
    });
  }

  resize(width: number, height: number) {
    this.app.renderer.resize(Math.max(300, Math.floor(width)), Math.max(260, Math.floor(height)));
    this.camera.setViewport(this.app.renderer.width, this.app.renderer.height);
    this.needsStaticRedraw = true;
  }

  screenToTile(screenX: number, screenY: number): { x: number; y: number } | null {
    if (!this.snapshot) return null;
    const scale = this.world.scale.x || 1;
    const localX = (screenX - this.world.x) / scale;
    const localY = (screenY - this.world.y) / scale;
    if (localX < 0 || localY < 0) return null;
    const x = Math.floor(localX / this.snapshot.board.tileSize);
    const y = Math.floor(localY / this.snapshot.board.tileSize);
    if (x < 0 || y < 0 || x >= this.snapshot.board.width || y >= this.snapshot.board.height) return null;
    return { x, y };
  }

  private drawTilesAndTerrain(snapshot: RenderSnapshot) {
    this.tilesLayer.removeChildren();
    this.terrainLayer.removeChildren();

    const tileSize = snapshot.board.tileSize;
    const skin = biomeSkinFor(snapshot.board.biomeId);

    const backdrop = new PIXI.Graphics();
    backdrop.rect(0, 0, snapshot.board.width * tileSize, snapshot.board.height * tileSize);
    backdrop.fill({ color: skin.tileBase, alpha: 1 });
    this.tilesLayer.addChild(backdrop);

    for (const tile of snapshot.tiles) {
      const x = tile.x * tileSize;
      const y = tile.y * tileSize;
      const base = tile.biomeVariant === "alt" ? skin.tileAlt : tile.biomeVariant === "path" ? skin.road : skin.tileBase;
      const depth = snapshot.board.height > 1 ? tile.y / (snapshot.board.height - 1) : 0.5;
      const litAlpha = 0.93 - (depth * 0.06);
      const shadowAlpha = 0.08 + (depth * 0.08);

      const cell = new PIXI.Graphics();
      cell.rect(x, y, tileSize, tileSize);
      cell.fill({ color: base, alpha: litAlpha });
      if (skin.tilePatternAlpha > 0) {
        const stripeAlpha = skin.tilePatternAlpha * (tile.biomeVariant === "alt" ? 0.9 : 0.65);
        cell.roundRect(x + 2, y + 2, tileSize - 4, 6, 2);
        cell.fill({ color: 0xffffff, alpha: stripeAlpha * (0.15 + (0.1 * (1 - depth))) });
        cell.roundRect(x + 2, y + tileSize - 8, tileSize - 4, 5, 2);
        cell.fill({ color: 0x000000, alpha: stripeAlpha * (0.16 + (0.08 * depth)) });
      }

      if (tile.overlays?.includes("water")) {
        cell.roundRect(x + 4, y + 4, tileSize - 8, tileSize - 8, 4);
        cell.fill({ color: skin.water, alpha: 0.35 + (0.08 * (1 - depth)) });
      }
      if (tile.overlays?.includes("hazard")) {
        cell.roundRect(x + 5, y + 5, tileSize - 10, tileSize - 10, 4);
        cell.fill({ color: skin.hazard, alpha: 0.26 + (0.06 * depth) });
      }
      if (tile.isBlocked) {
        cell.moveTo(x + 6, y + 6);
        cell.lineTo(x + tileSize - 6, y + tileSize - 6);
        cell.moveTo(x + tileSize - 6, y + 6);
        cell.lineTo(x + 6, y + tileSize - 6);
        cell.stroke({ color: 0xf9b6ba, width: 2, alpha: 0.55 });
      }
      cell.rect(x + 1, y + tileSize - 4, tileSize - 2, 3);
      cell.fill({ color: 0x000000, alpha: shadowAlpha * 0.45 });
      cell.rect(x, y, tileSize, tileSize);
      cell.stroke({ color: skin.grid, width: 1, alpha: 0.18 });
      this.tilesLayer.addChild(cell);
    }
  }

  private drawBackdrop(snapshot: RenderSnapshot) {
    const skin = biomeSkinFor(snapshot.board.biomeId);
    const lightingTint = snapshot.board.lighting?.tint ?? skin.lighting.tint ?? this.settings.backgroundFill;
    const fogAlpha = snapshot.board.lighting?.fogAlpha ?? 0.08;
    const vignetteAlpha = snapshot.board.lighting?.vignetteAlpha ?? 0.16;
    const width = this.app.renderer.width;
    const height = this.app.renderer.height;
    const pulse = this.settings.reducedMotion ? 0 : Math.sin(this.elapsedMs / 2100) * 0.03;
    const hazeAlpha = 0.16 + Math.min(0.14, fogAlpha * 0.8);
    const ambientFog = Math.min(0.28, 0.06 + (fogAlpha * 0.9));

    this.backdropLayer.clear();
    this.backdropLayer.rect(0, 0, width, height);
    this.backdropLayer.fill({ color: lightingTint, alpha: hazeAlpha + pulse });
    this.backdropLayer.rect(0, 0, width, height);
    this.backdropLayer.fill({ color: this.settings.backgroundFill, alpha: 0.24 + Math.min(0.12, fogAlpha) });
    this.backdropLayer.rect(0, 0, width, Math.max(40, Math.floor(height * 0.42)));
    this.backdropLayer.fill({ color: skin.fog, alpha: ambientFog });
    this.backdropLayer.rect(0, Math.floor(height * 0.6), width, Math.ceil(height * 0.4));
    this.backdropLayer.fill({ color: 0x040507, alpha: 0.2 + Math.min(0.12, vignetteAlpha * 0.7) });
    if (vignetteAlpha > 0) {
      const edgeAlpha = Math.min(0.36, vignetteAlpha + 0.08);
      const edge = 26;
      this.backdropLayer.rect(0, 0, width, edge).fill({ color: 0x000000, alpha: edgeAlpha });
      this.backdropLayer.rect(0, height - edge, width, edge).fill({ color: 0x000000, alpha: edgeAlpha });
      this.backdropLayer.rect(0, 0, edge, height).fill({ color: 0x000000, alpha: edgeAlpha });
      this.backdropLayer.rect(width - edge, 0, edge, height).fill({ color: 0x000000, alpha: edgeAlpha });
      this.backdropLayer.rect(0, 0, width, 2).fill({ color: 0xffffff, alpha: 0.03 + Math.max(0, pulse) });
    }
  }

  private drawProps(snapshot: RenderSnapshot) {
    this.propsLayer.removeChildren();
    const tileSize = snapshot.board.tileSize;

    const biomeProps = pickBiomeProps(snapshot).sort((left, right) => left.y - right.y);
    for (const prop of biomeProps) {
      const px = (prop.x * tileSize) + (tileSize / 2);
      const py = (prop.y * tileSize) + (tileSize / 2);
      const root = new PIXI.Container();
      root.position.set(px, py);

      const shadow = new PIXI.Graphics();
      shadow.ellipse(0, 9, tileSize * 0.14, tileSize * 0.08);
      shadow.fill({ color: 0x000000, alpha: 0.18 });
      root.addChild(shadow);

      const texture = this.assetManager.getTextureOrFallback(`prop:${prop.id}`, "prop", prop.tint, {
        biomeId: snapshot.board.biomeId,
        visualClass: "structure",
      });
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5, 0.8);
      sprite.width = Math.round(tileSize * 0.42);
      sprite.height = Math.round(tileSize * 0.42);
      sprite.tint = prop.tint;
      sprite.alpha = 0.8;
      root.addChild(sprite);

      if (prop.id.includes("torch") || prop.id.includes("lantern")) {
        const glow = new PIXI.Graphics();
        glow.circle(0, -Math.round(tileSize * 0.08), Math.round(tileSize * 0.2));
        glow.fill({ color: 0xffcb88, alpha: 0.22 });
        glow.blendMode = "add";
        root.addChild(glow);
      }

      this.propsLayer.addChild(root);
    }
  }

  private clearAtmosphere() {
    const toDestroy = this.atmosphereLayer.removeChildren();
    for (const child of toDestroy) {
      child.destroy();
    }
    this.atmosphereParticles = [];
  }

  private buildAtmosphere(snapshot: RenderSnapshot) {
    this.clearAtmosphere();
    if (this.settings.reducedMotion || this.settings.particleScale <= 0) return;

    const skin = biomeSkinFor(snapshot.board.biomeId);
    const tileArea = Math.max(1, snapshot.board.width * snapshot.board.height);
    const worldWidth = snapshot.board.width * snapshot.board.tileSize;
    const worldHeight = snapshot.board.height * snapshot.board.tileSize;
    const densityScale = qualityScale(this.settings.qualityMode)
      * Math.max(0.38, this.settings.particleScale)
      * Math.max(0.4, skin.ambientIntensity);
    const maxParticles = this.settings.qualityMode === "max"
      ? 180
      : this.settings.qualityMode === "perf"
        ? 74
        : 120;
    let allocated = 0;

    for (const emitter of skin.ambient) {
      const target = Math.round(tileArea * emitter.density * densityScale * 0.08);
      const count = Math.max(0, Math.min(maxParticles - allocated, target));
      if (count <= 0) continue;

      for (let idx = 0; idx < count; idx += 1) {
        const salt = `${snapshot.board.seed}:${emitter.id}:${idx}`;
        const sprite = new PIXI.Sprite(PIXI.Texture.WHITE);
        sprite.anchor.set(0.5, 0.5);
        sprite.position.set(
          seededRange(snapshot.board.seed, 0, worldWidth, `${salt}:x`),
          seededRange(snapshot.board.seed, 0, worldHeight, `${salt}:y`),
        );

        let scaleX = seededRange(snapshot.board.seed, 0.8, 1.8, `${salt}:sx`);
        let scaleY = seededRange(snapshot.board.seed, 0.7, 1.6, `${salt}:sy`);
        let speedMin = 8;
        let speedMax = 18;
        let baseAlpha = seededRange(snapshot.board.seed, 0.16, 0.34, `${salt}:alpha`);
        let blendMode: "normal" | "add" = "normal";

        if (emitter.id === "fireflies") {
          scaleX = seededRange(snapshot.board.seed, 1.0, 2.3, `${salt}:sx`);
          scaleY = scaleX;
          speedMin = 4;
          speedMax = 11;
          baseAlpha = seededRange(snapshot.board.seed, 0.22, 0.52, `${salt}:alpha`);
          blendMode = "add";
        } else if (emitter.id === "snow") {
          scaleX = seededRange(snapshot.board.seed, 0.9, 1.8, `${salt}:sx`);
          scaleY = seededRange(snapshot.board.seed, 1.3, 2.6, `${salt}:sy`);
          speedMin = 6;
          speedMax = 16;
          baseAlpha = seededRange(snapshot.board.seed, 0.2, 0.46, `${salt}:alpha`);
        } else if (emitter.id === "mist") {
          scaleX = seededRange(snapshot.board.seed, 2.4, 5.8, `${salt}:sx`);
          scaleY = seededRange(snapshot.board.seed, 1.0, 2.4, `${salt}:sy`);
          speedMin = 3;
          speedMax = 8;
          baseAlpha = seededRange(snapshot.board.seed, 0.08, 0.19, `${salt}:alpha`);
        } else if (emitter.id === "heat") {
          scaleX = seededRange(snapshot.board.seed, 2.0, 6.6, `${salt}:sx`);
          scaleY = seededRange(snapshot.board.seed, 0.45, 1.0, `${salt}:sy`);
          speedMin = 7;
          speedMax = 14;
          baseAlpha = seededRange(snapshot.board.seed, 0.09, 0.2, `${salt}:alpha`);
          blendMode = "add";
        } else if (emitter.id === "embers") {
          scaleX = seededRange(snapshot.board.seed, 0.9, 1.9, `${salt}:sx`);
          scaleY = scaleX;
          speedMin = 9;
          speedMax = 20;
          baseAlpha = seededRange(snapshot.board.seed, 0.18, 0.34, `${salt}:alpha`);
          blendMode = "add";
        }

        sprite.scale.set(scaleX, scaleY);
        sprite.tint = emitter.tint;
        sprite.alpha = baseAlpha;
        sprite.blendMode = blendMode;

        this.atmosphereLayer.addChild(sprite);
        this.atmosphereParticles.push({
          sprite,
          vx: seededRange(snapshot.board.seed, -speedMax, speedMax, `${salt}:vx`) * 0.33,
          vy: seededRange(snapshot.board.seed, speedMin * 0.15, speedMax, `${salt}:vy`) * (emitter.id === "snow" ? 1 : 0.4),
          baseAlpha,
          pulseRate: seededRange(snapshot.board.seed, 0.7, 2.2, `${salt}:pulse-rate`),
          pulseAmplitude: seededRange(snapshot.board.seed, 0.08, 0.28, `${salt}:pulse-amp`),
          phase: seededRange(snapshot.board.seed, 0, Math.PI * 2, `${salt}:phase`),
        });

        allocated += 1;
        if (allocated >= maxParticles) break;
      }

      if (allocated >= maxParticles) break;
    }
  }

  private updateAtmosphere(snapshot: RenderSnapshot, deltaMs: number) {
    if (this.atmosphereParticles.length === 0) return;
    const dt = deltaMs / 1000;
    const worldWidth = snapshot.board.width * snapshot.board.tileSize;
    const worldHeight = snapshot.board.height * snapshot.board.tileSize;
    const wrapPadding = 14;
    const fogAlpha = snapshot.board.lighting?.fogAlpha ?? 0.08;
    this.atmosphereLayer.alpha = this.settings.fastMode
      ? Math.min(0.72, 0.42 + (fogAlpha * 0.9))
      : Math.min(0.92, 0.56 + (fogAlpha * 0.95));

    for (const particle of this.atmosphereParticles) {
      particle.sprite.x += particle.vx * dt;
      particle.sprite.y += particle.vy * dt;

      if (particle.sprite.x < -wrapPadding) particle.sprite.x = worldWidth + wrapPadding;
      if (particle.sprite.x > worldWidth + wrapPadding) particle.sprite.x = -wrapPadding;
      if (particle.sprite.y < -wrapPadding) particle.sprite.y = worldHeight + wrapPadding;
      if (particle.sprite.y > worldHeight + wrapPadding) particle.sprite.y = -wrapPadding;

      const pulse = 1 + (Math.sin((this.elapsedMs * 0.001 * particle.pulseRate) + particle.phase) * particle.pulseAmplitude);
      particle.sprite.alpha = Math.max(0.02, Math.min(0.95, particle.baseAlpha * pulse));
    }
  }

  private drawUiMarkers(snapshot: RenderSnapshot) {
    this.uiLayer.removeChildren();
    const tileSize = snapshot.board.tileSize;
    const visibleMarkers = snapshot.board.type === "combat"
      ? snapshot.uiOverlays.filter((marker) => (
        marker.type === "danger"
          || marker.type === "objective"
          || marker.type === "hot_hook"
          || marker.priority <= 1
      ))
      : snapshot.uiOverlays;
    for (const marker of visibleMarkers) {
      const x = (marker.x * tileSize) + 2;
      const y = (marker.y * tileSize) + 2;
      const box = new PIXI.Graphics();
      box.roundRect(x, y, 18, 12, 3);
      box.fill({ color: 0x0f1115, alpha: 0.6 });
      box.stroke({ color: 0xfde68a, width: 1, alpha: 0.45 });
      this.uiLayer.addChild(box);

      const text = new PIXI.Text({
        text: marker.type === "danger" ? "!" : marker.type === "merchant" ? "$" : marker.type === "healer" ? "+" : "?",
        style: {
          fontFamily: "Verdana, sans-serif",
          fontSize: 9,
          fill: 0xfbf2ce,
          fontWeight: "bold",
        },
      });
      text.anchor.set(0.5, 0.5);
      text.position.set(x + 9, y + 6);
      this.uiLayer.addChild(text);
    }
  }

  private addTimedDisplay(display: PIXI.Container, lifeMs: number) {
    this.vfxLayer.addChild(display);
    this.activeTimed.push({ display, ageMs: 0, lifeMs });
  }

  private processEvent(event: VisualEvent) {
    if (!this.snapshot) return;
    this.consumedEventIds.add(event.id);
    this.debugState.eventTimeline.push(event);
    this.debugState.eventTimeline = this.debugState.eventTimeline.slice(-40);

    if (event.type === "BoardTransition") {
      this.transitions.start(event.fromBoardType, event.toBoardType, this.settings);
      return;
    }

    if (event.type === "TurnStart" && event.actorId) {
      const center = entityCenter(this.snapshot, event.actorId);
      if (center) this.camera.focus(center.x, center.y);
    }

    const anchor = (() => {
      if ("tile" in event && event.tile) return tileCenter(this.snapshot as RenderSnapshot, event.tile);
      if ("targetTile" in event && event.targetTile) return tileCenter(this.snapshot as RenderSnapshot, event.targetTile);
      if ("targetId" in event && event.targetId) return entityCenter(this.snapshot as RenderSnapshot, event.targetId);
      if ("entityId" in event && event.entityId) return entityCenter(this.snapshot as RenderSnapshot, event.entityId);
      if ("actorId" in event && event.actorId) return entityCenter(this.snapshot as RenderSnapshot, event.actorId);
      return null;
    })();

    if (anchor) {
      this.particles.emitFromEvent(event, anchor, this.settings);
      this.floatingText.emit(event, anchor, this.settings);
    }
    const intensity = importanceScale(event.fxImportance);

    const timedFxDurationScale = Math.max(0.25, this.settings.timedFxDurationScale || 1);
    if (event.type === "MoveTrail") {
      const line = new PIXI.Graphics();
      const from = tileCenter(this.snapshot, event.from);
      const to = tileCenter(this.snapshot, event.to);
      line.moveTo(from.x, from.y);
      line.lineTo(to.x, to.y);
      line.stroke({ color: 0x9be5ff, width: 2.2, alpha: 0.85 });
      this.addTimedDisplay(
        line,
        Math.min(1200, Math.max(280, event.durationMs * intensity * timedFxDurationScale)),
      );
    }

    if (event.type === "AttackWindup") {
      const source = entityCenter(this.snapshot, event.attackerId);
      const target = event.targetTile ? tileCenter(this.snapshot, event.targetTile) : null;
      if (source && target) {
        const line = new PIXI.Graphics();
        line.moveTo(source.x, source.y);
        line.lineTo(target.x, target.y);
        line.stroke({ color: 0xf6e2b8, width: 2.2, alpha: 0.8 });
        const tip = new PIXI.Graphics();
        tip.circle(target.x, target.y, 4);
        tip.fill({ color: 0xffe4b5, alpha: 0.8 });
        this.addTimedDisplay(line, Math.floor(260 * intensity * timedFxDurationScale));
        this.addTimedDisplay(tip, Math.floor(220 * intensity * timedFxDurationScale));
      }
    }

    if (event.type === "HitImpact") {
      this.camera.onHitImpact(Math.min(1, Math.max(0, event.damage / 120)), event.seedKey, this.settings);
      const flash = new PIXI.Graphics();
      flash.circle(anchor?.x ?? 0, anchor?.y ?? 0, 11 + (3 * intensity));
      flash.fill({ color: 0xffb09c, alpha: 0.44 });
      flash.stroke({ color: 0xfff4d8, width: 1.5, alpha: 0.5 });
      this.addTimedDisplay(flash, Math.floor(300 * intensity * timedFxDurationScale));
    }

    if (event.type === "HealImpact") {
      this.camera.onHealImpact(Math.min(1, Math.max(0, event.amount / 80)), this.settings);
      const pulse = new PIXI.Graphics();
      pulse.circle(anchor?.x ?? 0, anchor?.y ?? 0, 11);
      pulse.fill({ color: 0x9afab6, alpha: 0.34 });
      this.addTimedDisplay(pulse, Math.floor(340 * intensity * timedFxDurationScale));
    }

    if (event.type === "MissIndicator") {
      const miss = new PIXI.Graphics();
      miss.rect((anchor?.x ?? 0) - 9, (anchor?.y ?? 0) - 1, 18, 2);
      miss.fill({ color: 0xe9eef7, alpha: 0.7 });
      this.addTimedDisplay(miss, Math.floor(360 * intensity * timedFxDurationScale));
    }

    if (event.type === "DeathBurst" || event.type === "Downed") {
      const burst = new PIXI.Graphics();
      burst.circle(anchor?.x ?? 0, anchor?.y ?? 0, 14 + (4 * intensity));
      burst.stroke({ color: 0xff8ca6, width: 2, alpha: 0.9 });
      this.addTimedDisplay(burst, Math.floor(720 * intensity * timedFxDurationScale));
    }

    if (event.type === "TurnStart" || event.type === "TurnEnd") {
      const boardPulse = new PIXI.Graphics();
      if (this.snapshot) {
        boardPulse.rect(0, 0, this.snapshot.board.width * this.snapshot.board.tileSize, this.snapshot.board.height * this.snapshot.board.tileSize);
      }
      boardPulse.fill({ color: event.type === "TurnStart" ? 0x8be5ff : 0xfad28c, alpha: 0.08 });
      this.addTimedDisplay(boardPulse, Math.floor(240 * intensity * timedFxDurationScale));
    }
  }

  tick(deltaMs: number) {
    if (!this.snapshot) return;

    const frameMs = Math.max(1, deltaMs);
    this.elapsedMs += frameMs;
    this.fpsWindow.push(frameMs);
    if (this.fpsWindow.length > 60) this.fpsWindow.shift();
    const avgMs = this.fpsWindow.reduce((acc, value) => acc + value, 0) / this.fpsWindow.length;
    this.debugState.fps = avgMs > 0 ? 1000 / avgMs : 0;

    this.camera.setViewport(this.app.renderer.width, this.app.renderer.height);
    this.drawBackdrop(this.snapshot);

    if (this.needsStaticRedraw) {
      this.drawTilesAndTerrain(this.snapshot);
      this.drawProps(this.snapshot);
      this.buildAtmosphere(this.snapshot);
      this.needsStaticRedraw = false;
    }
    this.updateAtmosphere(this.snapshot, frameMs);

    this.entityRenderer.render(this.snapshot, this.assetManager, this.settings);
    const renderMeta = this.entityRenderer.getRenderMeta();
    this.telegraphRenderer.render(this.snapshot);
    this.drawUiMarkers(this.snapshot);

    const cadence = Math.max(32, Math.floor(this.settings.eventCadenceMs || (
      this.settings.fastMode
        ? 44
        : this.settings.qualityMode === "max"
          ? 84
          : this.settings.qualityMode === "perf"
            ? 130
            : 104
    )));
    this.eventAccumulatorMs += frameMs;
    while (this.eventAccumulatorMs >= cadence && this.pendingEvents.length > 0) {
      this.eventAccumulatorMs -= cadence;
      const next = this.pendingEvents.shift() as VisualEvent;
      this.processEvent(next);
    }

    const keep: TimedDisplay[] = [];
    for (const timed of this.activeTimed) {
      timed.ageMs += frameMs;
      if (timed.ageMs >= timed.lifeMs) {
        this.vfxLayer.removeChild(timed.display);
        timed.display.destroy();
        continue;
      }
      const alpha = Math.max(0, 1 - (timed.ageMs / timed.lifeMs));
      timed.display.alpha = alpha;
      keep.push(timed);
    }
    this.activeTimed = keep;

    this.particles.update(frameMs);
    this.floatingText.update(frameMs);
    this.transitions.update(frameMs);
    this.transitions.drawOverlay(this.transitionLayer, this.app.renderer.width, this.app.renderer.height);

    const transform = this.camera.update(frameMs, this.settings);
    this.camera.applyTo(this.world, transform);
    const cameraDebug = this.camera.debugState();

    this.debugState.drawCalls = this.app.stage.children.length + this.world.children.length;
    this.debugState.queueDepth = this.pendingEvents.length;
    this.debugState.activeParticles = this.particles.activeCount() + this.atmosphereParticles.length;
    this.debugState.activeFloatingTexts = this.floatingText.activeCount();
    this.debugState.uiDensity = renderMeta.uiDensity;
    this.debugState.tokenLabelMode = renderMeta.tokenLabelMode;
    this.debugState.statusChipMode = renderMeta.statusChipMode;
    this.debugState.intentChipMode = renderMeta.intentChipMode;
    this.debugState.cameraScale = cameraDebug.scale;
    this.debugState.cameraShakeMs = cameraDebug.shakeMs;

    this.devOverlay.render(this.snapshot, this.debugState, this.settings.showDevOverlay);
    this.app.render();
  }

  getDebugState(): RendererDebugState {
    return {
      fps: this.debugState.fps,
      drawCalls: this.debugState.drawCalls,
      eventTimeline: [...this.debugState.eventTimeline],
      queueDepth: this.debugState.queueDepth,
      activeParticles: this.debugState.activeParticles,
      activeFloatingTexts: this.debugState.activeFloatingTexts,
      uiDensity: this.debugState.uiDensity,
      tokenLabelMode: this.debugState.tokenLabelMode,
      statusChipMode: this.debugState.statusChipMode,
      intentChipMode: this.debugState.intentChipMode,
      cameraScale: this.debugState.cameraScale,
      cameraShakeMs: this.debugState.cameraShakeMs,
    };
  }

  destroy() {
    this.clearAtmosphere();
    this.entityRenderer.destroy();
    this.telegraphRenderer.destroy();
    this.particles.destroy();
    this.floatingText.destroy();
    this.devOverlay.destroy();
    this.assetManager.destroy();
    this.app.destroy(true, { children: true, texture: false, textureSource: false, context: false });
  }
}
